import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingApi, cruiseApi, invoiceApi } from '../lib/api';
import { StatusBadge, RouteBadge, InvoiceStatusBadge } from '../components/StatusBadge';
import { 
  Anchor, Search, Calendar, Ship, LogOut, User, 
  Clock, MapPin, Plus, Minus, X, Check, FileText, Download, Filter, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function AgentDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [stats, setStats] = useState(null);
  const [creditSummary, setCreditSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchParams, setSearchParams] = useState({
    route: '',
    duration_nights: '',
  });
  
  // Booking filters
  const [bookingStatusFilter, setBookingStatusFilter] = useState('');
  const [bookingDateFrom, setBookingDateFrom] = useState(null);
  const [bookingDateTo, setBookingDateTo] = useState(null);
  
  // Invoice filters
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState(null);
  const [invoiceDateTo, setInvoiceDateTo] = useState(null);
  
  // Booking modal state
  const [bookingModal, setBookingModal] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [selectedCruise, setSelectedCruise] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cart, setCart] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'bookings') {
      loadBookings();
    } else if (activeTab === 'invoices') {
      loadInvoices();
    }
  }, [activeTab, bookingStatusFilter, bookingDateFrom, bookingDateTo, invoiceStatusFilter, invoiceDateFrom, invoiceDateTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, creditRes] = await Promise.all([
        bookingApi.getStats(),
        bookingApi.getAll({}),
        invoiceApi.getCreditSummary().catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setCreditSummary(creditRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      const params = {};
      if (bookingStatusFilter && bookingStatusFilter !== 'all') params.status = bookingStatusFilter;
      if (bookingDateFrom) params.date_from = format(bookingDateFrom, 'yyyy-MM-dd');
      if (bookingDateTo) params.date_to = format(bookingDateTo, 'yyyy-MM-dd');
      
      const res = await bookingApi.getAll(params);
      setBookings(res.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const params = {};
      if (invoiceStatusFilter && invoiceStatusFilter !== 'all') params.status = invoiceStatusFilter;
      if (invoiceDateFrom) params.date_from = format(invoiceDateFrom, 'yyyy-MM-dd');
      if (invoiceDateTo) params.date_to = format(invoiceDateTo, 'yyyy-MM-dd');
      
      const res = await invoiceApi.getAll(params);
      setInvoices(res.data);
      
      // Also refresh credit summary
      const creditRes = await invoiceApi.getCreditSummary().catch(() => ({ data: null }));
      setCreditSummary(creditRes.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const params = {
        route: searchParams.route && searchParams.route !== 'all' ? searchParams.route : null,
        departure_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
        duration_nights: searchParams.duration_nights && searchParams.duration_nights !== 'all' ? parseInt(searchParams.duration_nights) : null,
      };
      
      const response = await cruiseApi.search(params);
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No cruises found matching your criteria');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const openBookingModal = (cruise) => {
    setSelectedCruise(cruise);
    setCart([]);
    setBookingModal(true);
  };

  const openInvoiceModal = async (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceModal(true);
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await invoiceApi.downloadPdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const addToCart = (item, type) => {
    const existing = cart.find(c => c.accommodation_id === item.id);
    if (existing) {
      if (existing.quantity < item.available_quantity) {
        setCart(cart.map(c => 
          c.accommodation_id === item.id 
            ? { ...c, quantity: c.quantity + 1 }
            : c
        ));
      }
    } else {
      setCart([...cart, {
        accommodation_type: type,
        accommodation_id: item.id,
        accommodation_name: item.name,
        quantity: 1,
        price_per_night: item.price_per_night,
        max_quantity: item.available_quantity,
      }]);
    }
  };

  const removeFromCart = (itemId) => {
    const existing = cart.find(c => c.accommodation_id === itemId);
    if (existing && existing.quantity > 1) {
      setCart(cart.map(c => 
        c.accommodation_id === itemId 
          ? { ...c, quantity: c.quantity - 1 }
          : c
      ));
    } else {
      setCart(cart.filter(c => c.accommodation_id !== itemId));
    }
  };

  const calculateTotal = () => {
    if (!selectedCruise) return 0;
    return cart.reduce((sum, item) => {
      return sum + (item.price_per_night * selectedCruise.duration_nights * item.quantity);
    }, 0);
  };

  const handleBooking = async () => {
    if (cart.length === 0) {
      toast.error('Please select at least one cabin or suite');
      return;
    }

    setBookingLoading(true);
    try {
      const bookingData = {
        cruise_id: selectedCruise.cruise_id,
        schedule_id: selectedCruise.schedule_id,
        departure_date: selectedCruise.departure_date,
        items: cart.map(item => ({
          accommodation_type: item.accommodation_type,
          accommodation_id: item.accommodation_id,
          accommodation_name: item.accommodation_name,
          quantity: item.quantity,
          price_per_night: item.price_per_night,
          total_price: item.price_per_night * selectedCruise.duration_nights * item.quantity,
        })),
      };

      const response = await bookingApi.create(bookingData);
      toast.success(`Booking created! Number: ${response.data.booking_number}`);
      setBookingModal(false);
      setCart([]);
      loadData();
      setActiveTab('bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await bookingApi.cancel(bookingId);
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const clearBookingFilters = () => {
    setBookingStatusFilter('');
    setBookingDateFrom(null);
    setBookingDateTo(null);
  };

  const clearInvoiceFilters = () => {
    setInvoiceStatusFilter('');
    setInvoiceDateFrom(null);
    setInvoiceDateTo(null);
  };

  const routeLabels = {
    luxor_to_aswan: 'Luxor → Aswan',
    aswan_to_luxor: 'Aswan → Luxor',
  };

  return (
    <div className="agent-layout">
      {/* Top Bar */}
      <header className="agent-topbar">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <span className="logo-text text-xl hidden sm:block">
              Nile Cruise <span className="logo-accent">Plus</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">{user?.name}</span>
            {user?.company_name && (
              <span className="text-slate-400">| {user.company_name}</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-500 hover:text-slate-700"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="agent-main" data-testid="agent-dashboard">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="stat-card animate-fade-in">
            <div className="stat-value">{stats?.total_bookings || 0}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
          <div className="stat-card animate-fade-in stagger-1">
            <div className="stat-value text-yellow-600">{stats?.pending_bookings || 0}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card animate-fade-in stagger-2">
            <div className="stat-value text-green-600">{stats?.confirmed_bookings || 0}</div>
            <div className="stat-label">Confirmed</div>
          </div>
          <div className="stat-card animate-fade-in stagger-3">
            <div className="stat-value">${stats?.total_value?.toLocaleString() || 0}</div>
            <div className="stat-label">Total Value</div>
          </div>
          <div className="stat-card animate-fade-in stagger-4">
            <div className="stat-value text-amber-600">${creditSummary?.balance_due?.toLocaleString() || 0}</div>
            <div className="stat-label">Balance Due</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 mb-6">
            <TabsTrigger 
              value="search" 
              className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white"
              data-testid="search-tab"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </TabsTrigger>
            <TabsTrigger 
              value="bookings"
              className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white"
              data-testid="bookings-tab"
            >
              <Ship className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="invoices"
              className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white"
              data-testid="invoices-tab"
            >
              <FileText className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search" className="mt-0">
            {/* Search Form */}
            <div className="search-form-glass p-6 mb-8">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Route</Label>
                  <Select
                    value={searchParams.route}
                    onValueChange={(value) => setSearchParams({ ...searchParams, route: value })}
                  >
                    <SelectTrigger className="h-12" data-testid="route-select">
                      <SelectValue placeholder="All Routes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Routes</SelectItem>
                      <SelectItem value="luxor_to_aswan">Luxor → Aswan</SelectItem>
                      <SelectItem value="aswan_to_luxor">Aswan → Luxor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Departure Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal"
                        data-testid="date-picker-trigger"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-600">Duration</Label>
                  <Select
                    value={searchParams.duration_nights}
                    onValueChange={(value) => setSearchParams({ ...searchParams, duration_nights: value })}
                  >
                    <SelectTrigger className="h-12" data-testid="duration-select">
                      <SelectValue placeholder="Any Duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Duration</SelectItem>
                      <SelectItem value="2">2 Nights</SelectItem>
                      <SelectItem value="3">3 Nights</SelectItem>
                      <SelectItem value="4">4 Nights</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    className="w-full h-12 btn-primary"
                    onClick={handleSearch}
                    disabled={searchLoading}
                    data-testid="search-btn"
                  >
                    {searchLoading ? (
                      <span className="spinner w-5 h-5 border-2" />
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Search Results */}
            <div className="space-y-4">
              {searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <div 
                    key={`${result.cruise_id}-${result.schedule_id}-${result.departure_date}`}
                    className="cruise-card animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`cruise-result-${index}`}
                  >
                    <div className="flex flex-col md:flex-row">
                      <div 
                        className="w-full md:w-64 h-48 md:h-auto bg-cover bg-center"
                        style={{ backgroundImage: `url(${result.cruise_image || 'https://images.unsplash.com/photo-1713144453052-0cc2518faa4a?w=400'})` }}
                      />
                      <div className="flex-1 p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-heading text-xl font-bold text-[#0F172A]">
                              {result.cruise_name}
                            </h3>
                            <p className="text-slate-500 text-sm mt-1">{result.cruise_description}</p>
                          </div>
                          <RouteBadge route={result.route} />
                        </div>

                        <div className="flex flex-wrap gap-4 mb-4 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {result.departure_date}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {result.duration_nights} nights
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {routeLabels[result.route]}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          {result.cabins.map((cabin) => (
                            <div 
                              key={cabin.id}
                              className={`p-3 rounded-sm border ${cabin.available_quantity > 0 ? 'border-slate-200 bg-slate-50' : 'border-slate-100 bg-slate-100 opacity-60'}`}
                            >
                              <div className="text-xs text-slate-500 uppercase tracking-wide">Cabin</div>
                              <div className="font-medium text-sm">{cabin.name}</div>
                              <div className="text-[#1D4ED8] font-bold">${cabin.price_per_night}/night</div>
                              <div className={`text-xs ${cabin.available_quantity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {cabin.available_quantity > 0 ? `${cabin.available_quantity} available` : 'Sold out'}
                              </div>
                            </div>
                          ))}
                          {result.suites.map((suite) => (
                            <div 
                              key={suite.id}
                              className={`p-3 rounded-sm border-2 ${suite.available_quantity > 0 ? 'border-[#D4AF37]/30 bg-[#F3E5AB]/10' : 'border-slate-100 bg-slate-100 opacity-60'}`}
                            >
                              <div className="text-xs text-[#D4AF37] uppercase tracking-wide font-semibold">Suite</div>
                              <div className="font-medium text-sm">{suite.name}</div>
                              <div className="text-[#D4AF37] font-bold">${suite.price_per_night}/night</div>
                              <div className={`text-xs ${suite.available_quantity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {suite.available_quantity > 0 ? `${suite.available_quantity} available` : 'Sold out'}
                              </div>
                            </div>
                          ))}
                        </div>

                        <Button
                          className="btn-gold"
                          onClick={() => openBookingModal(result)}
                          data-testid={`book-cruise-${index}`}
                        >
                          Book Now
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <Ship className="empty-state-icon" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">Search for Cruises</h3>
                  <p className="text-sm">Use the search form above to find available Nile cruises</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="mt-0">
            {/* Filters */}
            <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm">Filter by Date</span>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <Select value={bookingStatusFilter} onValueChange={setBookingStatusFilter}>
                  <SelectTrigger data-testid="booking-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {bookingDateFrom ? format(bookingDateFrom, 'MMM d, yyyy') : 'From Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={bookingDateFrom}
                      onSelect={setBookingDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {bookingDateTo ? format(bookingDateTo, 'MMM d, yyyy') : 'To Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={bookingDateTo}
                      onSelect={setBookingDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button variant="ghost" onClick={clearBookingFilters} className="text-slate-500">
                  Clear Filters
                </Button>
              </div>
            </div>

            {bookings.length > 0 ? (
              <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="table-nile">
                    <thead>
                      <tr>
                        <th>Booking #</th>
                        <th>Cruise</th>
                        <th>Route</th>
                        <th>Departure</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id} data-testid={`booking-row-${booking.id}`}>
                          <td className="font-mono text-sm">{booking.booking_number}</td>
                          <td className="font-medium">{booking.cruise_name}</td>
                          <td>
                            <RouteBadge route={booking.route} />
                          </td>
                          <td>{booking.departure_date}</td>
                          <td className="font-bold">${booking.total_amount.toLocaleString()}</td>
                          <td>
                            <StatusBadge status={booking.status} />
                          </td>
                          <td>
                            {booking.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleCancelBooking(booking.id)}
                                data-testid={`cancel-booking-${booking.id}`}
                              >
                                Cancel
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <Ship className="empty-state-icon" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Bookings Yet</h3>
                <p className="text-sm">Search for cruises and make your first booking</p>
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-0">
            {/* Credit Summary */}
            {creditSummary && (
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-sm border border-blue-100">
                  <p className="text-xs text-blue-600 uppercase font-medium">Total Invoiced</p>
                  <p className="text-2xl font-bold text-blue-700">${creditSummary.total_invoiced?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-sm border border-green-100">
                  <p className="text-xs text-green-600 uppercase font-medium">Total Paid</p>
                  <p className="text-2xl font-bold text-green-700">${creditSummary.total_paid?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-sm border border-amber-100">
                  <p className="text-xs text-amber-600 uppercase font-medium">Balance Due</p>
                  <p className="text-2xl font-bold text-amber-700">${creditSummary.balance_due?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                  <p className="text-xs text-slate-600 uppercase font-medium">Pending Invoices</p>
                  <p className="text-2xl font-bold text-slate-700">{creditSummary.pending_invoices || 0}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm">Filter Invoices</span>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                  <SelectTrigger data-testid="invoice-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="issued">Issued</SelectItem>
                    <SelectItem value="partially_paid">Partially Paid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {invoiceDateFrom ? format(invoiceDateFrom, 'MMM d, yyyy') : 'Due From'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={invoiceDateFrom}
                      onSelect={setInvoiceDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {invoiceDateTo ? format(invoiceDateTo, 'MMM d, yyyy') : 'Due To'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={invoiceDateTo}
                      onSelect={setInvoiceDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button variant="ghost" onClick={clearInvoiceFilters} className="text-slate-500">
                  Clear Filters
                </Button>
              </div>
            </div>

            {invoices.length > 0 ? (
              <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="table-nile">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Booking</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Due Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                          <td className="font-mono text-sm">{invoice.invoice_number}</td>
                          <td className="font-mono text-sm">{invoice.booking_number}</td>
                          <td className="font-bold">${invoice.total_amount.toLocaleString()}</td>
                          <td className="text-green-600">${invoice.amount_paid.toLocaleString()}</td>
                          <td className={invoice.balance_due > 0 ? 'text-amber-600 font-bold' : 'text-green-600'}>
                            ${invoice.balance_due.toLocaleString()}
                          </td>
                          <td>{invoice.due_date}</td>
                          <td><InvoiceStatusBadge status={invoice.status} /></td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openInvoiceModal(invoice)}
                                data-testid={`view-invoice-${invoice.id}`}
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadPdf(invoice.id)}
                                data-testid={`download-invoice-${invoice.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Invoices Yet</h3>
                <p className="text-sm">Invoices will appear here once your bookings are confirmed</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Modal */}
      <Dialog open={bookingModal} onOpenChange={setBookingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Book: {selectedCruise?.cruise_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCruise && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="badge-route">{routeLabels[selectedCruise.route]}</span>
                  <span className="px-3 py-1 bg-slate-100 rounded-full">{selectedCruise.departure_date}</span>
                  <span className="px-3 py-1 bg-slate-100 rounded-full">{selectedCruise.duration_nights} nights</span>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-700">Cabins</h4>
                  {selectedCruise.cabins.map((cabin) => (
                    <div key={cabin.id} className="accommodation-item">
                      <div>
                        <div className="font-medium">{cabin.name}</div>
                        <div className="text-sm text-slate-500">
                          ${cabin.price_per_night}/night × {selectedCruise.duration_nights} nights | Max {cabin.max_occupancy} guests
                        </div>
                        <div className="text-sm text-green-600">{cabin.available_quantity} available</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeFromCart(cabin.id)}
                          disabled={!cart.find(c => c.accommodation_id === cabin.id)}
                          data-testid={`remove-cabin-${cabin.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">
                          {cart.find(c => c.accommodation_id === cabin.id)?.quantity || 0}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addToCart(cabin, 'cabin')}
                          disabled={cabin.available_quantity === 0 || (cart.find(c => c.accommodation_id === cabin.id)?.quantity || 0) >= cabin.available_quantity}
                          data-testid={`add-cabin-${cabin.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedCruise.suites.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#D4AF37]">Suites</h4>
                    {selectedCruise.suites.map((suite) => (
                      <div key={suite.id} className="accommodation-item gold-border">
                        <div>
                          <div className="font-medium">{suite.name}</div>
                          <div className="text-sm text-slate-500">
                            ${suite.price_per_night}/night × {selectedCruise.duration_nights} nights | Max {suite.max_occupancy} guests
                          </div>
                          <div className="text-sm text-green-600">{suite.available_quantity} available</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeFromCart(suite.id)}
                            disabled={!cart.find(c => c.accommodation_id === suite.id)}
                            data-testid={`remove-suite-${suite.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">
                            {cart.find(c => c.accommodation_id === suite.id)?.quantity || 0}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addToCart(suite, 'suite')}
                            disabled={suite.available_quantity === 0 || (cart.find(c => c.accommodation_id === suite.id)?.quantity || 0) >= suite.available_quantity}
                            data-testid={`add-suite-${suite.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {cart.length > 0 && (
                  <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                    <h4 className="font-semibold mb-3">Your Selection</h4>
                    {cart.map((item) => (
                      <div key={item.accommodation_id} className="flex justify-between text-sm py-1">
                        <span>{item.quantity}x {item.accommodation_name}</span>
                        <span className="font-medium">
                          ${(item.price_per_night * selectedCruise.duration_nights * item.quantity).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-[#1D4ED8]">${calculateTotal().toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBookingModal(false)}>
              Cancel
            </Button>
            <Button
              className="btn-gold"
              onClick={handleBooking}
              disabled={cart.length === 0 || bookingLoading}
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? (
                <span className="spinner w-5 h-5 border-2" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Modal */}
      <Dialog open={invoiceModal} onOpenChange={setInvoiceModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Invoice Details</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Invoice Number</p>
                  <p className="font-mono font-medium">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <InvoiceStatusBadge status={selectedInvoice.status} />
                </div>
                <div>
                  <p className="text-slate-500">Due Date</p>
                  <p className="font-medium">{selectedInvoice.due_date}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Cruise</p>
                  <p className="font-medium">{selectedInvoice.cruise_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Booking</p>
                  <p className="font-mono text-sm">{selectedInvoice.booking_number}</p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-slate-500 text-sm mb-2">Items</p>
                {selectedInvoice.items?.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm py-1">
                    <span>{item.quantity}x {item.accommodation_name}</span>
                    <span className="font-medium">${item.total_price?.toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 mt-2 pt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>${selectedInvoice.subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Tax ({((selectedInvoice.tax_rate || 0) * 100).toFixed(1)}%)</span>
                    <span>${selectedInvoice.tax_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Total</span>
                    <span>${selectedInvoice.total_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Paid</span>
                    <span>${selectedInvoice.amount_paid?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-bold text-amber-600">
                    <span>Balance Due</span>
                    <span>${selectedInvoice.balance_due?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-medium mb-2">Payment History</p>
                  {selectedInvoice.payments.map((payment, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-100">
                      <span>{payment.created_at?.slice(0, 10)} - {payment.payment_method?.replace('_', ' ')}</span>
                      <span className="text-green-600">${payment.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => handleDownloadPdf(selectedInvoice.id)}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
